import { Css, ScrollableParent, Step, useComputed } from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { StepperProvider, stepperBarHeight, useStepperContext } from "src/components/stepper";
import { BidScopeSource, Maybe, SaveBidPackageInput } from "src/generated/graphqlTypes";
import {
  AddBidPackagePageQuery,
  AddBidPackagePage_BidPackagesFragment,
  AddBidPackagePage_OriginatingProjectFragment,
  useAddBidPackagePageQuery,
  useAddBidPackagePage_OriginatingProjectQuery,
} from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import { BidPackageParams } from "src/routes/routesDef";
import { isDefined, isEmptyObject, queryResult, zeroTo } from "src/utils";
import { BooleanParam, StringParam, useQueryParams } from "use-query-params";
import { BidPackageDetailsStep } from "./components/1.BidPackageDetailsStep";
import { ReviewScopeStep } from "./components/2.ReviewScopeStep";
import { ReviewUnitBidScopeStep } from "./components/2.ReviewUnitBidScopeStep";
import { SelectBiddersStep } from "./components/3.SelectBiddersStep";
import { ReviewAndSendStep } from "./components/5.ReviewAndSendStep";
import { BidPackageStepActions } from "./components/BidPackageStepActions";
import { BidPackageStep } from "./utils";

export function AddBidPackagePage() {
  const { bidPackageGroupId } = useParams<BidPackageParams>();
  const [{ projectId, isInternalEstimate }] = useQueryParams({
    projectId: StringParam,
    isInternalEstimate: BooleanParam,
  });
  const query = useAddBidPackagePageQuery({ variables: { id: bidPackageGroupId || "" }, skip: !bidPackageGroupId });

  const originatingProjectQuery = useAddBidPackagePage_OriginatingProjectQuery({
    variables: { id: projectId! },
    skip: !!bidPackageGroupId || !projectId,
  });

  if (!bidPackageGroupId) {
    return projectId ? (
      queryResult(originatingProjectQuery, ({ project }) => (
        <StepperProvider steps={initialSteps(true, isInternalEstimate)}>
          <AddBidPackageView originatingProject={project} />
        </StepperProvider>
      ))
    ) : (
      <StepperProvider steps={initialSteps(true, isInternalEstimate)}>
        <AddBidPackageView />
      </StepperProvider>
    );
  }

  // Don't disable steps in `edit` mode on stepper initialization
  // Our formState useComputed validation will still check required fields if edits are made per step
  return queryResult(query, ({ bidPackageGroup }) => (
    <StepperProvider steps={initialSteps(!bidPackageGroup, bidPackageGroup.bidPackages.first!.isInternalEstimate)}>
      <AddBidPackageView bidPackageGroup={bidPackageGroup} />
    </StepperProvider>
  ));
}

type AddBidPackageViewProps = {
  bidPackageGroup?: AddBidPackagePageQuery["bidPackageGroup"];
  originatingProject?: AddBidPackagePage_OriginatingProjectFragment;
};

function AddBidPackageView({ bidPackageGroup, originatingProject }: AddBidPackageViewProps) {
  const { bidPackageGroupId: savedGroupId } = useParams<BidPackageParams>();
  const [{ isInternalEstimate: queryIsInternalEstimate }] = useQueryParams({ isInternalEstimate: BooleanParam });
  const isInternalEstimate = bidPackageGroup
    ? bidPackageGroup.bidPackages.first!.isInternalEstimate
    : queryIsInternalEstimate;
  const { currentStep, setSteps } = useStepperContext();
  const [stepIndex, setStepIndex] = useState(0);
  const formState = useFormState({
    config: formConfig,
    init: {
      input: bidPackageGroup,
      map: (bidPackageGroup) => mapBidPackagesToForm(bidPackageGroup.bidPackages, isInternalEstimate),
      ifUndefined: {
        marketId: originatingProject?.market?.id,
        scopeSource: BidScopeSource.ProductOffering,
        bidPackages: [{ delete: undefined }],
        isInternalEstimate: isInternalEstimate ?? false,
      },
    },
  });

  // This updates the continue/enable/disable state of the stepper based on required formstate
  useComputed(() => {
    if (formState.dirty && !isEmptyObject(formState.changedValue)) {
      const coreFieldsEmpty =
        !formState.marketId.value ||
        formState.bidPackages.value?.isEmpty ||
        !formState.costType.valid ||
        !formState.scopeSource.valid ||
        ([BidScopeSource.Lot, BidScopeSource.Subdivision, BidScopeSource.Community].includes(
          formState.scopeSource.value!,
        ) &&
          !formState.projects.value?.length);

      // If we are an internal estimate, we only have 3 steps (Package Details, Review Scope, Review + Send)
      if (formState.isInternalEstimate.value) {
        setSteps((prevState) => [
          { ...prevState[0], disabled: false },
          { ...prevState[1], disabled: coreFieldsEmpty },
          {
            ...prevState[2],
            // Todo: disable if no line items
            disabled: coreFieldsEmpty,
          },
        ]);
      } else {
        setSteps((prevState) => [
          { ...prevState[0], disabled: false },
          { ...prevState[1], disabled: coreFieldsEmpty },
          {
            ...prevState[2],
            // Todo: disable if no line items
            disabled: false,
          },
          {
            ...prevState[3],
            // A bid category has been selected and has at least one trade partner
            disabled:
              formState.bidPackages.value.isEmpty ||
              formState.bidPackages.value.some((bc) => !bc.requests || bc.requests.isEmpty),
          },
        ]);
      }
    }
  }, [formState]);

  useEffect(() => {
    const currentStepIndex = initialSteps(!isDefined(savedGroupId), isInternalEstimate).findIndex(
      (step) => step.value === currentStep.value,
    );
    if (stepIndex === currentStepIndex) return;
    setStepIndex(currentStepIndex);
    // If we are an internal estimate, we only have 3 steps (Package Details, Review Scope, Review + Send)
    // Otherwise, we have 4 steps (Package Details, Review Scope, Select Bidders, Review + Send)
    setSteps((prevState) =>
      zeroTo(isInternalEstimate ? 3 : 4, (i) => ({
        ...prevState[i],
        state: currentStepIndex > [i, 3].min() ? "complete" : prevState[i].state,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const step = useMemo(() => {
    switch (currentStep.value) {
      case BidPackageStep.PACKAGE_DETAILS:
        return <BidPackageDetailsStep formState={formState} originatingProject={originatingProject} />;
      case BidPackageStep.REVIEW_SCOPE:
        return formState.isUnitBased.value ? (
          <ReviewUnitBidScopeStep formState={formState} />
        ) : (
          <ReviewScopeStep formState={formState} />
        );
      case BidPackageStep.SELECT_BIDDERS:
        return <SelectBiddersStep formState={formState} />;
      case BidPackageStep.REVIEW_AND_SEND:
        return <ReviewAndSendStep formState={formState} />;
      default:
        return <BidPackageDetailsStep formState={formState} />;
    }
  }, [currentStep.value, formState]);

  return (
    <ScrollableParent px={8} xss={Css.mbPx(stepperBarHeight).$}>
      {step}
      <BidPackageStepActions formState={formState} />
    </ScrollableParent>
  );
}

const INCOMPLETE = "incomplete" as const;
/* If we are creating an internal estimate, we don't need to select bidders or contacts */
export const initialSteps: (isCreateMode: boolean, isInternalEstimate?: Maybe<boolean>) => Step[] = (
  isCreateMode = true,
  isInternalEstimate = false,
) => [
  { value: BidPackageStep.PACKAGE_DETAILS, label: "Package Details", state: INCOMPLETE, disabled: isCreateMode },
  { value: BidPackageStep.REVIEW_SCOPE, label: "Review Scope", state: INCOMPLETE, disabled: isCreateMode },
  ...(isInternalEstimate
    ? []
    : [{ value: BidPackageStep.SELECT_BIDDERS, label: "Select Bidders", state: INCOMPLETE, disabled: isCreateMode }]),
  { value: BidPackageStep.REVIEW_AND_SEND, label: "Review + Send", state: INCOMPLETE, disabled: isCreateMode },
];

export type BidPackageFormState = Pick<
  SaveBidPackageInput,
  "bidPackageGroupId" | "costType" | "isUnitBased" | "projects" | "scopeSource" | "isInternalEstimate"
> & { marketId?: Maybe<string>; bidPackages: Pick<SaveBidPackageInput, "requests" | "id" | "costCodes" | "delete">[] };

export const formConfig: ObjectConfig<BidPackageFormState> = {
  marketId: { type: "value", rules: [required] },
  projects: { type: "value" },
  isUnitBased: { type: "value" },
  bidPackageGroupId: { type: "value" },
  isInternalEstimate: { type: "value" },
  costType: { type: "value", rules: [required] },
  scopeSource: { type: "value", rules: [required] },
  bidPackages: {
    type: "list",
    config: {
      id: { type: "value" },
      costCodes: { type: "value", rules: [required] },
      delete: { type: "value" },
      requests: {
        type: "list",
        config: {
          id: { type: "value" },
          tradePartnerId: { type: "value" },
          primaryContactId: { type: "value", rules: [] },
          secondaryContactId: { type: "value" },
        },
      },
    },
    rules: [required],
  },
};

export function mapBidPackagesToForm(
  bidPackages: AddBidPackagePage_BidPackagesFragment[],
  isInternalEstimateParam?: Maybe<boolean>,
) {
  const {
    bidPackageGroup,
    // Deconstruct date vals due to strict typing on formState config
    dueDate,
    estimatedAwardDate,
    estimatedConstructionDate,
    sendReminderDate,
    // Deconstruct to reformat for formState
    projects,
    isInternalEstimate,
    costType,
    scopeSource,
  } = bidPackages?.first ?? {};
  return {
    bidPackageGroupId: bidPackageGroup?.id,
    ...bidPackages?.first,
    costType: costType?.code,
    scopeSource: scopeSource?.code,
    dueDate: dueDate ?? null,
    estimatedAwardDate: estimatedAwardDate ?? null,
    estimatedConstructionDate: estimatedConstructionDate ?? null,
    sendReminderDate: sendReminderDate ?? null,
    // Even if the bid package is for specific projects we still want to render the market selector
    marketId: projects?.first?.market?.id,
    projects: projects?.map((p) => p.id),
    bidPackages: (bidPackages ?? []).map((bp) => ({
      id: bp.id,
      isUnitBased: bp.isUnitBased ?? false,
      costCodes: bp.costCodes.map((cc) => cc.id),
      isInternalEstimate: isInternalEstimate
        ? isInternalEstimate
        : isInternalEstimateParam
          ? isInternalEstimateParam
          : false,
      delete: false,
      requests: bp.requests.map((r) => ({
        id: r.id,
        tradePartnerId: r.tradePartner.id,
        primaryContactId: r.primaryContact?.id,
        secondaryContactId: r.secondaryContact?.id,
      })),
    })),
  };
}
